<template>
  <div class="container-fluid bg-white vh-100 p-0">
    <div class="d-flex flex-column align-items-center min-vh-50">
      <div class="col-11 col-sm-10 col-md-8 col-lg-6 col-xl-5 p-3">
        <div class="d-flex align-items-center">
          <span><i class="text-muted bi bi-question-circle me-2"></i></span>
          <span class="text-muted text-xs">
            Please upload the required supporting documents for your
            application. Ensure all documents are clear and legible.
          </span>
        </div>

        <div class="form-group mt-3">
          <!-- Application Type Section -->
          <div class="mb-4 p-3 border rounded shadow-sm">
            <h5 class="fw-bold mb-3">Application Type</h5>
            <div class="alert alert-info mb-0 py-2 text-xs">
              <i class="bi bi-info-circle me-2"></i>
              <strong>{{ applicant_type }}</strong> ({{ transaction_type }})
              <span class="text-muted ms-2"
                >- Based on your Personal Details</span
              >
            </div>
          </div>

          <!-- File Upload Sections -->
          <template v-for="docKey in document_keys" :key="docKey">
            <div
              v-if="should_show_section(docKey)"
              class="mb-4 p-3 border rounded shadow-sm"
            >
              <h5 class="fw-bold mb-1">
                {{ get_label(docKey) }}
                <span v-if="is_required(docKey)" class="text-danger">*</span>
              </h5>
              <small class="text-muted text-xs d-block mb-3">
                {{ get_description(docKey) }}
                <span class="fw-bold"
                  >(Max {{ max_files[docKey] }}
                  {{ max_files[docKey] > 1 ? "files" : "file" }})</span
                >
              </small>

              <!-- Upload Area -->
              <div
                class="border border-2 border-dashed rounded p-4 text-center bg-light"
                :class="{
                  'border-success bg-success bg-opacity-10':
                    get_files_for_key(docKey).length > 0,
                  'border-primary bg-primary bg-opacity-10':
                    is_dragging[docKey],
                }"
                @dragover.prevent="is_dragging[docKey] = true"
                @dragleave.prevent="is_dragging[docKey] = false"
                @drop.prevent="handle_file_drop($event, docKey)"
              >
                <div v-if="get_files_for_key(docKey).length === 0">
                  <i class="bi bi-cloud-arrow-up fs-1 text-muted"></i>
                  <p class="mb-2 mt-2 fw-bold text-xs text-muted">
                    Drag and drop your files here
                  </p>
                  <p class="text-muted text-xs mb-3">or</p>
                  <button
                    type="button"
                    class="btn btn-bg-green rounded-3 shadow-sm text-xs btn-sm"
                    @click="trigger_file_input(docKey)"
                  >
                    <i class="bi bi-folder2-open me-1"></i> Browse Files
                  </button>
                  <p class="text-muted mt-3 text-xs mb-0">
                    Supported: PDF, JPG, JPEG, PNG
                  </p>
                </div>

                <div v-else>
                  <div class="mb-3">
                    <i
                      class="bi bi-check-circle-fill text-success fs-4 me-2"
                    ></i>
                    <span class="fw-bold text-xs">
                      {{ get_files_for_key(docKey).length }}
                      {{
                        get_files_for_key(docKey).length === 1
                          ? "file"
                          : "files"
                      }}
                      uploaded
                    </span>
                  </div>

                  <!-- File List -->
                  <div
                    class="text-start"
                    style="max-height: 200px; overflow-y: auto"
                  >
                    <div
                      v-for="(file, i) in get_files_for_key(docKey)"
                      :key="docKey + '_file_' + i"
                      class="d-flex align-items-center justify-content-between p-2 mb-2 bg-white border rounded"
                    >
                      <div
                        class="d-flex align-items-center flex-grow-1 overflow-hidden"
                      >
                        <i
                          class="bi bi-file-earmark-text text-primary me-2"
                        ></i>
                        <span
                          class="text-truncate text-xs"
                          :title="file.name"
                          >{{ file.name }}</span
                        >
                        <span
                          v-if="file.is_existing"
                          class="badge bg-info ms-2 text-xs"
                          >Saved</span
                        >
                      </div>
                      <div class="d-flex align-items-center ms-2">
                        <span class="text-muted text-xs me-2">{{
                          format_file_size(file.size)
                        }}</span>
                        <button
                          type="button"
                          class="btn btn-sm btn-link text-primary p-0 me-2"
                          @click="view_file(file)"
                          title="View file"
                        >
                          <i class="bi bi-eye"></i>
                        </button>
                        <button
                          type="button"
                          class="btn btn-sm btn-link text-danger p-0"
                          @click="remove_file(docKey, i)"
                          title="Remove file"
                        >
                          <i class="bi bi-x-circle"></i>
                        </button>
                      </div>
                    </div>
                  </div>

                  <div
                    class="mt-3"
                    v-if="get_files_for_key(docKey).length < max_files[docKey]"
                  >
                    <button
                      type="button"
                      class="btn btn-bg-info rounded-3 shadow-sm text-xs btn-sm"
                      @click="trigger_file_input(docKey)"
                    >
                      <i class="bi bi-plus-circle me-1"></i> Add More Files
                    </button>
                  </div>
                </div>
              </div>

              <input
                :ref="
                  (el) => {
                    if (el) file_inputs[docKey] = el;
                  }
                "
                type="file"
                :id="docKey"
                class="d-none"
                @change="handle_file_change($event, docKey)"
                accept=".pdf,.jpg,.jpeg,.png"
                :multiple="max_files[docKey] > 1"
              />
              <small
                v-if="error_messages[docKey]"
                class="form-text text-danger text-xs fw-bold mt-2 d-block"
              >
                {{ error_messages[docKey] }}
              </small>
            </div>
          </template>

          <!-- Validation Warning -->
          <div class="mb-4" v-if="!all_required_filled && has_attempted_submit">
            <div class="alert alert-warning py-2 text-xs mb-0">
              <i class="bi bi-exclamation-triangle me-2"></i>
              Please upload all required documents before proceeding.
            </div>
          </div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="text-center mb-3 mt-4">
        <button
          type="button"
          @click="previous"
          class="btn btn-bg-secondary rounded-3 shadow-sm text-xs btn-sm me-2"
        >
          Previous
        </button>
        <button
          type="button"
          :disabled="!all_required_filled || is_loading"
          @click="upload_documents"
          class="btn btn-bg-green rounded-3 shadow-sm text-xs btn-sm"
        >
          <span v-if="is_loading">
            <span
              class="spinner-border spinner-border-sm me-1"
              role="status"
            ></span>
            Uploading...
          </span>
          <span v-else>Complete Application</span>
        </button>
      </div>
    </div>
  </div>
</template>
<script src="./support_documentation.js"></script>

<style scoped>
/* Border dashed utility */
.border-dashed {
  border-style: dashed !important;
}

/* Button Styles */
.btn-bg-green {
  background-color: #28a745;
  color: white;
  border: none;
}

.btn-bg-green:hover {
  background-color: #218838;
  color: white;
}

.btn-bg-green:disabled {
  background-color: #94d3a2;
  cursor: not-allowed;
}

.btn-bg-secondary {
  background-color: #6c757d;
  color: white;
  border: none;
}

.btn-bg-secondary:hover {
  background-color: #5a6268;
  color: white;
}

.btn-bg-info {
  background-color: #17a2b8;
  color: white;
  border: none;
}

.btn-bg-info:hover {
  background-color: #138496;
  color: white;
}

.btn-bg-warning {
  background-color: #ffc107;
  color: #212529;
  border: none;
}

.btn-bg-warning:hover {
  background-color: #e0a800;
  color: #212529;
}

/* Text utilities */
.text-xs {
  font-size: 0.75rem;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .d-flex.align-items-center.justify-content-between {
    flex-direction: column;
    align-items: flex-start !important;
    gap: 8px;
  }
}
</style>
